import {
  type PermitBatch,
  type PermitSingle,
  permit2Abi,
  permitBatchAbi,
} from "@eulerxyz/core-sdk"
import type { ContractFunctionConfig } from "@eulerxyz/evc-sdk"
import { BaseTransactionFactory } from "./BaseTransactionFactory"
import {
  type PermitBatchTransactionItem,
  type PermitSingleTransactionItem,
  type PermitTransactionItem,
  isPermitBatchTransactionItem,
  isPermitSingleTransactionItem,
} from "./types"

export class Permit2TransactionFactory extends BaseTransactionFactory {
  typeGuardConvertMultiple(
    _permit2Transactions: PermitTransactionItem[],
  ): ContractFunctionConfig[] {
    const contractCallReadyTxs: ContractFunctionConfig[] = []
    for (const tx of _permit2Transactions) {
      const call = this.typeGuardConvertSingle(tx)
      contractCallReadyTxs.push(call)
    }
    return contractCallReadyTxs
  }

  typeGuardConvertSingle(
    transaction: PermitTransactionItem,
  ): ContractFunctionConfig {
    if (isPermitSingleTransactionItem(transaction)) {
      return this.convertPermitSingle(transaction)
    }

    if (isPermitBatchTransactionItem(transaction)) {
      return this.convertPermitBatch(transaction)
    }

    throw this.errorUnsupportedTransactionType(transaction)
  }

  convertPermitSingle(
    permitSingle: PermitSingleTransactionItem,
  ): ContractFunctionConfig<typeof permit2Abi, "permit"> {
    const singleDeserialized: PermitSingle = {
      ...permitSingle.permit,
      details: {
        ...permitSingle.permit.details,
        amount: BigInt(permitSingle.permit.details.amount),
      },
      sigDeadline: BigInt(permitSingle.permit.sigDeadline),
    }

    return {
      address: permitSingle.address,
      abi: permit2Abi,
      functionName: "permit",
      args: [permitSingle.from, singleDeserialized, permitSingle.signature],
      onBehalfOfAccount: permitSingle.from,
      value: 0n, // since in this case the token is not native
    }
  }

  private convertPermitBatch(
    permitBatch: PermitBatchTransactionItem,
  ): ContractFunctionConfig<typeof permitBatchAbi, "permit"> {
    const batchDeserialized: PermitBatch = {
      ...permitBatch.permit,
      sigDeadline: BigInt(permitBatch.permit.sigDeadline),
      details: permitBatch.permit.details.map((detail) => ({
        ...detail,
        amount: BigInt(detail.amount),
      })),
    }

    return {
      address: permitBatch.address,
      abi: permitBatchAbi,
      functionName: "permit",
      args: [permitBatch.from, batchDeserialized, permitBatch.signature],
      onBehalfOfAccount: permitBatch.from,
      value: 0n, // since in this case the token is not native
    }
  }
}
